/**
 * "Volt túlóra?" / "Túlóra jelentése" — the driver reports overtime minutes
 * after the fact.
 *
 * Opens by itself right after a successful "Letettem" (with "Nem volt" to
 * dismiss), and again from the finished trip's "Túlóra jelentése" button —
 * which is offered only while the booking is still Confirmed
 * (portalFormat's `canReportOvertime`): once it is Completed, by the office
 * or the nightly auto-complete, the trip takes no more input. Each submit sends
 * one `overtime_reported` (minutes 1–600, optional note ≤ 300 chars, capped
 * by the field itself, and where it happened — "A célnál" unless the driver
 * picks "Felvételnél"); several are allowed.
 *
 * Minutes only. The sending itself (single-flight lock, location, error copy)
 * belongs to the trip screen: `onSubmit` resolves to true once the API has
 * taken it.
 */
import React, { useId, useState } from 'react';
import {
  OVERTIME_MAX_MINUTES,
  OVERTIME_MIN_MINUTES,
  OVERTIME_NOTE_MAX,
  OVERTIME_STAGE_LABELS,
  OVERTIME_STAGES,
  parseOvertimeMinutes,
} from '../portalFormat';
import BottomSheet from './BottomSheet';

const MINUTES_ERROR = `Adj meg egész percet ${OVERTIME_MIN_MINUTES} és ${OVERTIME_MAX_MINUTES} között.`;

export default function OvertimeReportSheet({
  title = 'Volt túlóra?',
  dismissLabel = 'Nem volt',
  busy = false,
  error = '',
  onSubmit,
  onDismiss,
}) {
  const minutesId = useId();
  const noteId = useId();
  const stageId = useId();
  const [stage, setStage] = useState('dropoff');
  const [minutes, setMinutes] = useState('');
  const [note, setNote] = useState('');
  const [invalid, setInvalid] = useState('');

  const submit = (event) => {
    event.preventDefault();
    if (busy) return;
    const value = parseOvertimeMinutes(minutes);
    if (!Number.isFinite(value)) {
      setInvalid(MINUTES_ERROR);
      return;
    }
    setInvalid('');
    onSubmit({ minutes: value, note, stage });
  };

  const shown = invalid || error;

  return (
    <BottomSheet
      title={title}
      subtitle="Csak a perceket add meg — a díjat az iroda számolja."
      onClose={onDismiss}
      closeDisabled={busy}
    >
      <form className="pp-form" onSubmit={submit} noValidate>
        <div className="pp-field">
          <span className="pp-field__label" id={stageId}>Hol?</span>
          <div className="pp-seg" role="group" aria-labelledby={stageId}>
            {OVERTIME_STAGES.map((option) => (
              <button
                key={option}
                type="button"
                className={`pp-seg__item${stage === option ? ' pp-seg__item--on' : ''}`}
                aria-pressed={stage === option}
                onClick={() => setStage(option)}
                disabled={busy}
              >
                {OVERTIME_STAGE_LABELS[option]}
              </button>
            ))}
          </div>
        </div>

        <div className="pp-field">
          <label className="pp-field__label" htmlFor={minutesId}>
            Túlóra (perc)
          </label>
          <input
            id={minutesId}
            className="pp-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            placeholder="Pl. 20"
            value={minutes}
            onChange={(event) => { setMinutes(event.target.value); setInvalid(''); }}
            aria-invalid={Boolean(invalid) || undefined}
            disabled={busy}
          />
        </div>

        <div className="pp-field">
          <label className="pp-field__label" htmlFor={noteId}>
            Megjegyzés
            <span className="pp-field__optional"> · nem kötelező</span>
          </label>
          <textarea
            id={noteId}
            className="pp-input pp-input--area"
            rows={2}
            maxLength={OVERTIME_NOTE_MAX}
            placeholder="Pl. az utas késett a szállodából"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={busy}
          />
        </div>

        {shown ? <div className="pp-form__error" role="alert">{shown}</div> : null}

        <div className="pp-actions">
          <button
            type="button"
            className="pp-btn pp-btn--outline pp-btn--grow pp-btn--tall"
            onClick={onDismiss}
            disabled={busy}
          >
            {dismissLabel}
          </button>
          <button
            type="submit"
            className="pp-btn pp-btn--primary pp-btn--grow pp-btn--tall"
            disabled={busy}
          >
            {busy ? 'Küldés…' : 'Hozzáadás'}
          </button>
        </div>
      </form>
    </BottomSheet>
  );
}
